import { useEffect, useState } from 'react';
import { ApiService } from './api';

// Hosts that serve the full platform; any other host may be an event's custom domain.
const PLATFORM_HOSTS = (process.env.REACT_APP_PLATFORM_HOSTS
  || 'event.nextwavesaas.tech,www.event.nextwavesaas.tech,localhost')
  .split(',').map((host) => host.trim().toLowerCase()).filter(Boolean);

const PORTAL_KEY = 'teamPortalEventId';
const CACHE_KEY = 'customDomainSite';

export const isPlatformHost = (hostname = window.location.hostname) => {
  const host = (hostname || '').toLowerCase();
  if (!host || PLATFORM_HOSTS.includes(host)) return true;
  // LAN servers and dev machines are reached by IP or .local names.
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host) || host.endsWith('.local') || !host.includes('.')) return true;
  return false;
};

let pending = null;

const readCache = () => {
  try {
    const cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
    return cached?.host === window.location.hostname ? cached : null;
  } catch (_) {
    return null;
  }
};

export const resolveCustomDomain = () => {
  if (isPlatformHost()) return Promise.resolve(null);
  if (!pending) {
    pending = ApiService.resolveDomain(window.location.hostname)
      .then((site) => {
        const value = site?.eventId ? site : null;
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ host: window.location.hostname, site: value })); } catch (_) { /* storage unavailable */ }
        return value;
      })
      .catch(() => {
        pending = null;
        return null;
      });
  }
  return pending;
};

/** { status: 'platform' | 'loading' | 'event' | 'unknown', site } for the current host. */
export const useCustomDomain = () => {
  const [state, setState] = useState(() => {
    if (isPlatformHost()) return { status: 'platform', site: null };
    const cached = readCache();
    if (cached) return { status: cached.site ? 'event' : 'unknown', site: cached.site };
    return { status: 'loading', site: null };
  });

  useEffect(() => {
    if (state.status !== 'loading') return undefined;
    let active = true;
    resolveCustomDomain().then((site) => {
      if (active) setState({ status: site ? 'event' : 'unknown', site });
    });
    return () => { active = false; };
  }, [state.status]);

  return state;
};

/** Remembers which event's sign-in page the user came from so sign-out returns there. */
export const rememberPortal = (eventId) => {
  try {
    if (eventId) localStorage.setItem(PORTAL_KEY, eventId);
    else localStorage.removeItem(PORTAL_KEY);
  } catch (_) { /* storage unavailable */ }
};

export const signInPath = () => {
  if (!isPlatformHost()) return '/login';
  try {
    const eventId = localStorage.getItem(PORTAL_KEY);
    return eventId ? `/team/${encodeURIComponent(eventId)}` : '/auth';
  } catch (_) {
    return '/auth';
  }
};
